import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import TGButton from 'src/components/ui/TGButton';
import { TooltipItem } from 'src/utils/Tooltip';
import Loading from 'src/components/Loading';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import TextAreaVPO from '../../ui/textArea';
import { addDataVPO, updateDataVPO, fetchInconsistenciesByActionIdRequest } from '../../store/formAddActions/actions';
import { selectLastAddedId, selectIsLastAddedByCreator, selectInconsistenciesByAction } from '../../store/formAddActions/selectors';
import { shallowEqual } from 'react-redux';
import AddActionInComponent from 'src/pages/DashboardPage/mytasks/addAction/inComponent';

interface Inconsistence {
  id: number;
  text: string;
  isSubmitted: boolean;
}

interface InconsistencesProps {
  isRequired?: boolean;
  value?: string;
  index: number;
  inconsistenceId?: number;
  inconsistenceDocumentId?: string;
  disabled?: boolean;
  firstActionId?: number;
  editMode?: boolean;
  inconsistenceData?: any;
}

const selectIdAddVersionVPO = (state: any) => selectLastAddedId(state, 'vpo-actions');
const selectIdVPOInconsistences = (state: any) => selectLastAddedId(state, 'vpo-inconsistencies');
const selectActionData = (state: any) => {
  const vpoActionsData = state.vpoStrapi.formAddActions['vpo-actions'];
  if (vpoActionsData?.lastAddedId) {
    return { id: vpoActionsData.lastAddedId };
  }
  return null;
};

export const Inconsistences = React.memo(({
  isRequired = true,
  value,
  index,
  inconsistenceId,
  inconsistenceDocumentId,
  disabled,
  firstActionId,
  editMode = false,
  inconsistenceData
}: InconsistencesProps) => {
  const dispatch = useAppDispatch();

  const componentId = useRef(`inconsistences_${index}_${Date.now()}_${Math.random()}`).current;
  const idAddVersionVPO = useAppSelector(selectIdAddVersionVPO);
  const idVPOInconsistences = useAppSelector(selectIdVPOInconsistences);
  const actionData = useAppSelector(selectActionData, shallowEqual);
  const loadedInconsistencies = useAppSelector(selectInconsistenciesByAction);

  const effectiveActionId = firstActionId || actionData?.id || idAddVersionVPO;

  const isLastAddedByThisComponent = useAppSelector(
    (state) => selectIsLastAddedByCreator(state, 'vpo-inconsistencies', componentId)
  );

  const [inconsistenceState, setInconsistenceState] = useState({
    currentInconsistency: '',
    inconsistences: [] as Inconsistence[],
    newInconsistencyText: '',
    localInconsistenceId: null as number | null,
    isInconsistenceReady: false,
    isEditing: false,
    savedInconsistenceId: null as number | null,
  });

  const defaultValues = useMemo(() => ({
    inconsistency: value || '',
  }), [value]);

  const { handleSubmit, control, setValue, reset } = useForm({ defaultValues });

  const decodeHTMLEntities = useCallback((text: string) => {
    if (!text) return text;
    const tempElement = document.createElement('div');
    tempElement.innerHTML = text;
    return tempElement.textContent;
  }, []);

  const onSubmitIncons = useCallback(async (data: { inconsistency: string }) => {
    const inconsistencyValue = !isRequired && !data.inconsistency ? '-' : data.inconsistency;
    if (isRequired && !inconsistencyValue) return;

    const { localInconsistenceId, isEditing, savedInconsistenceId } = inconsistenceState;

    if (effectiveActionId && inconsistenceDocumentId) {
      dispatch(updateDataVPO('vpo-inconsistencies', inconsistenceDocumentId, {
        Inconsistences: inconsistencyValue,
      }));
    } else if (effectiveActionId && savedInconsistenceId) {
      dispatch(updateDataVPO('vpo-inconsistencies', savedInconsistenceId, {
        Inconsistences: inconsistencyValue,
      }));
    } else if (localInconsistenceId) {
      dispatch(updateDataVPO('vpo-inconsistencies', localInconsistenceId, {
        Inconsistences: inconsistencyValue,
      }));
    } else if (effectiveActionId && !inconsistenceDocumentId && !savedInconsistenceId) {
      setInconsistenceState(prev => ({ ...prev, newInconsistencyText: inconsistencyValue }));

      dispatch(addDataVPO('vpo-inconsistencies', {
        Inconsistences: inconsistencyValue,
        IdVPOactions: effectiveActionId,
      }, componentId));
    } else if (!isEditing && idAddVersionVPO) {
      setInconsistenceState(prev => ({
        ...prev,
        currentInconsistency: inconsistencyValue,
        isEditing: true
      }));

      const formId = idAddVersionVPO;
      dispatch(addDataVPO('vpo-inconsistencies', {
        Inconsistences: inconsistencyValue,
        IdVPOactions: formId,
      }, componentId));
    }
  }, [
    isRequired,
    inconsistenceState,
    effectiveActionId,
    inconsistenceId,
    inconsistenceDocumentId,
    dispatch,
    idAddVersionVPO,
    componentId
  ]);

  useEffect(() => {
    if (value) {
      setValue('inconsistency', value);
    }
  }, [value, setValue]);

  useEffect(() => {
    if (!firstActionId) {
      setInconsistenceState({
        currentInconsistency: '',
        inconsistences: [],
        newInconsistencyText: '',
        localInconsistenceId: null,
        isInconsistenceReady: false,
        isEditing: false,
        savedInconsistenceId: null,
      });
      return;
    }
    dispatch(fetchInconsistenciesByActionIdRequest(firstActionId));
  }, [firstActionId, dispatch]);

  useEffect(() => {
    if (!loadedInconsistencies || loadedInconsistencies.length === 0) {
      reset({ inconsistency: '' });
      setInconsistenceState(prev => ({
        ...prev,
        savedInconsistenceId: null,
        localInconsistenceId: null,
      }));
      return;
    }

    if (!firstActionId) return;

    const timer = setTimeout(() => {
      const currentCardInconsistencies = loadedInconsistencies.filter(
        (inc: any) => inc.IdVPOactions === firstActionId
      );

      if (currentCardInconsistencies.length === 0) return;

      const inconsistency = currentCardInconsistencies[index];
      if (!inconsistency) return;

      if (inconsistency.IdVPOactions !== firstActionId) return;
      if (inconsistency.Inconsistences) {
        setValue('inconsistency', inconsistency.Inconsistences);
      }
      setInconsistenceState(prev => ({
        ...prev,
        savedInconsistenceId: inconsistency.id,
        localInconsistenceId: inconsistency.id,
      }));
    }, 900); 

    return () => clearTimeout(timer);
  }, [loadedInconsistencies, firstActionId, index, setValue, reset]);

  useEffect(() => {
    if (editMode && inconsistenceData) {
      if (inconsistenceData.Inconsistences) {
        setValue('inconsistency', inconsistenceData.Inconsistences);
      }
      setInconsistenceState(prev => ({
        ...prev,
        savedInconsistenceId: inconsistenceData.id,
        localInconsistenceId: inconsistenceData.id,
      }));
    }
  }, [editMode, inconsistenceData, setValue]);

  useEffect(() => {
    if (!idVPOInconsistences || !isLastAddedByThisComponent) return;
    const { currentInconsistency, newInconsistencyText } = inconsistenceState;
    setInconsistenceState(prev => ({
      ...prev,
      localInconsistenceId: idVPOInconsistences,
      savedInconsistenceId: idVPOInconsistences, 
      isInconsistenceReady: true,
    }));

    if (currentInconsistency) {
      setInconsistenceState(prev => ({
        ...prev,
        inconsistences: [
          ...prev.inconsistences,
          {
            id: idVPOInconsistences,
            text: currentInconsistency,
            isSubmitted: true,
          }
        ],
        currentInconsistency: '',
      }));
    } else if (actionData && newInconsistencyText) {
      setInconsistenceState(prev => ({
        ...prev,
        inconsistences: [
          ...prev.inconsistences,
          {
            id: idVPOInconsistences,
            text: newInconsistencyText,
            isSubmitted: true,
          }
        ],
        newInconsistencyText: '',
      }));
    }
  }, [idVPOInconsistences, isLastAddedByThisComponent, inconsistenceState.currentInconsistency, inconsistenceState.newInconsistencyText, actionData]);

  const currentInconsistenceId = inconsistenceId || inconsistenceState.savedInconsistenceId || inconsistenceState.localInconsistenceId;

  const ActionComponent = useMemo(() => {
    if (!effectiveActionId) {
      return (
        <div className='mt-4'>
          <h3 className='text-[#637381] font-medium mb-4'>Действия</h3>
          <AddActionInComponent
            key="new-action"
            typeModule={'VPO'}
            idModule={0}
            CustomStyles={'bg-white p-5'}
            customTypes={undefined}
          />
        </div>
      );
    }

    return (
      <div className='mt-4'>
        <h3 className='text-[#637381] font-medium mb-2.5'>Действия</h3>
        <AddActionInComponent
          key={`action-${currentInconsistenceId || 'temp'}`}
          typeModule={'VPO'}
          idModule={currentInconsistenceId || 0}
          CustomStyles={'bg-white p-5'}
          customTypes={currentInconsistenceId ? `add_action_in_component_${currentInconsistenceId}` : undefined}
        />
      </div>
    );
  }, [effectiveActionId, currentInconsistenceId]);

  const SubmitButton = useMemo(() => {
    if (!effectiveActionId) {
      return (
        <TooltipItem
          wrapperClassName='w-full'
          position='top'
          tooltipsText={'Сохраните форму, чтобы добавить несоответствие и действия'}
        >
          <TGButton
            className='w-full text-gray-400 bg-transparent mt-6'
            type='button'
            disabled={true}
          >
            Сохранить несоответствие
          </TGButton>
        </TooltipItem>
      );
    }

    return (
      <TGButton className='w-full mt-6' type='submit'>
        Сохранить несоответствие
      </TGButton>
    );
  }, [effectiveActionId]);

  return (
    <div className='space-y-4'>
      <div className='space-y-4'>
        <div className='p-5 bg-gray-100 rounded-lg'>
          <form onSubmit={handleSubmit(onSubmitIncons)}>
            <Controller
              name='inconsistency'
              control={control}
              rules={{ required: isRequired }}
              render={({ field, fieldState: { error } }) => (
                <TextAreaVPO
                  dataField={{
                    type: 'textArea',
                    require: isRequired,
                    width: 'w-full',
                    title: 'Несоответствие',
                    key: 'inconsistency',
                    placeholder: isRequired
                      ? 'Опишите несоответствие требованию'
                      : 'Опишите несоответствие требованию (не обязательное поле)',
                    disabled: !effectiveActionId,
                  }}
                  value={decodeHTMLEntities(field.value)}
                  onChange={field.onChange}
                  errors={{ inconsistency: error }}
                />
              )}
            />
            {SubmitButton}
          </form>
          <div className='mt-4'>
            {ActionComponent}
          </div>
        </div>
      </div>
    </div>
  );
});